import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";

const caseStudies = {
  troll: {
    title: "Dealing with a Troll",
    subtitle: "How to handle disruptive editors on Wikipedia",
    image: "https://customer-assets.emergentagent.com/job_ethicalwiki-client/artifacts/lb5cwv37_about-bg.webp",
    sections: [
      {
        title: "Introduction",
        content: "According to Wikipedia, a troll is someone that is deliberately disruptive to the site, often in order to inflame or invite conflict. Wikipedia points out that it's common for companies and individuals to see crowd-sourced editors making unwanted edits and call them a 'troll.' Often, the subject of the article is biased, not the alleged troll. This case study addresses what Wikipedia calls a 'Point of View Warrior' - someone determined to push a non-neutral depiction on readers. In this case, the client's business changes and political statements led to online attacks from customers and activists."
      },
      {
        title: "The Rules",
        content: "Wikipedia generally won't sanction editors just for making bad edits - most new editors make poor edits until they gain experience. Editors are more commonly blocked for behavioral issues. The rule of law on Wikipedia is 'consensus.' Editors will be sanctioned when they insist on making edits contrary to consensus. For example, an editor who reverts others three times within 24 hours may be blocked."
      },
      {
        title: "The Reality",
        content: "Understanding this situation requires seeing things from the volunteer editor's perspective. If a corporation asks administrators to block a volunteer from editing their page, it looks like corporate censorship. Many companies don't understand they don't control their Wikipedia page. Any given administrator hasn't spent time on the page or experienced the problem editor firsthand."
      },
      {
        title: "The Strategy",
        content: "Point-of-view warriors can only be defeated by 'consensus.' We needed several editors to disagree with the one making poor edits. The approach was to focus on small, specific changes that are clearly supported by Wikipedia's rules - changes everyone would support. Then, let editors experience the disruptive behavior and make their own judgments."
      },
      {
        title: "The Execution",
        content: "Fighting off the troll took multiple months. The client posted at Wikipedia's Neutral Point of View Noticeboard, drawing two editors who were immediately reverted. We followed up on the Reliable Sources Noticeboard, questioning specific citations. Gradually, editors trickled in, including a customer who found the article by chance. Eventually, three editors saw clearly that one person was bent on destroying the client's reputation. After consensus formed, the change was made, but the troll kept reverting. They reverted two different editors three times, leading to page protection. The troll never returned. Six months after starting, patience paid off with a substantially improved page."
      }
    ]
  },
  "edit-war": {
    title: "Ending a Multi-Year Edit-War",
    subtitle: "Turning conflict into collaboration",
    image: "https://customer-assets.emergentagent.com/job_ethicalwiki-client/artifacts/l803ts6t_punch.webp",
    sections: [
      {
        title: "Introduction",
        content: "A billion-dollar corporation was in a protracted edit-war with a competing small business over their Wikipedia page content. Neither party was transparent about their relationship to the company. Both had violated Wikipedia's content policies. Both were frustrated, reverting each other and adding biased content. On Wikipedia this is an 'edit-war,' but going to war wasn't the answer."
      },
      {
        title: "The Rules",
        content: "Wikipedia's Terms of Use require editors to disclose their employer, client, and affiliation for any compensated contributions. Wikipedia's Conflict of Interest Guideline covers a broader range - those with an 'external role or relationship' that could undermine their primary role as an editor. The competitor had an external role that could interfere with neutrality."
      },
      {
        title: "The Reality",
        content: "Wikipedia accepts that editors most likely to contribute to a page are often industry insiders who may also be best qualified to write about the topic. Someone in the same industry but not employed by the company may have some affiliation with competitors. Wikipedia doesn't want to ban people from editing articles about companies in their industry. The key difference is whether someone is acting in a personal or professional capacity."
      },
      {
        title: "The Strategy",
        content: "The client initially wanted to fight in 'Wikipedia court' for sanctions against the other editor. However, the competitor's violations were fairly trivial content issues - editors are mostly blocked for behavioral violations. The client was actually more likely to be sanctioned themselves. Instead, we took a diplomatic approach. Despite contentious editing, very little communication had occurred. We planned to focus on content and citations together."
      },
      {
        title: "The Execution",
        content: "First, we authored a complete, revamped draft with 50+ citations, including the controversies the other editor cared about but more balanced overall. We worked through the content section-by-section with the competitor over 10+ pages of discussion, finding compromises and getting independent opinions. The result was extraordinary: a neutral, comprehensive page ranked among the top 1% on Wikipedia with the 'Good Article' designation. We also established a friendly, collaborative relationship with the other editor for future changes. Conflict was natural given our interests, but patience and civility created a win-win for everyone."
      }
    ]
  },
  "new-page": {
    title: "Creating a New Page",
    subtitle: "Navigating Wikipedia's notability requirements",
    image: "https://customer-assets.emergentagent.com/job_ethicalwiki-client/artifacts/ejcokl2a_movingup-3.webp",
    sections: [
      {
        title: "Introduction",
        content: "Almost 20 years ago, two people started a website that began as a basic forum. Over the years, it grew into a job board, blog, forum, social network, and professional resources hub for millions of users. The website was covered in mainstream media and academic journals. Its growing popularity signaled trends in their profession. But did this online community qualify for a Wikipedia page? That's the perennial question for new pages."
      },
      {
        title: "The Rules",
        content: "Wikipedia requires that a subject be famous enough to qualify for a page. The formal requirements include five elements: significant coverage in multiple independent reliable secondary sources. In practice, these criteria are open to wide interpretation. More than 75% of prospective clients who inquire about new pages don't meet the requirements. Wikipedians often consider informal factors like company size and age - typically $150 million in revenue and no longer a 'startup.'"
      },
      {
        title: "The Reality",
        content: "The challenge was with 'significant' coverage. The website had a mainstream press feature story, but not a long, in-depth profile. It was mentioned in academic journals and other media, but whether it qualified was borderline - different editors could reasonably argue either way."
      },
      {
        title: "The Strategy",
        content: "We crafted a small, neutral page. Small pages are expected for smaller, less famous companies. Over-sized pages appear vain and require lower-quality citations. Since a volunteer has to review the submission, a short article with strong citations makes review easier. We avoided anything promotional - no awards, philanthropy, or off-topic information. Volunteer reviewers reject entire drafts with promotional content rather than cleaning them up. We emphasized patience, persistence, and civility throughout the process."
      },
      {
        title: "The Execution",
        content: "We researched, wrote, and coded a draft Wikipedia page. What followed was messy - typical of crowd-sourced websites. An administrator deleted the client's user profile disclosure within minutes. The submission was initially rejected by an overseas editor who focused on the client's connection to the subject rather than the content. The client was patient through repeated requests for disclosure they had already provided many times. Eventually, by steering discussions toward the actual content and citations, the reviewing editor approved the page without changes. All it took was patience and savvy to get individualized attention."
      }
    ]
  },
  balancing: {
    title: "Balancing a Controversial Page",
    subtitle: "Creating comprehensive coverage beyond controversies",
    image: "https://customer-assets.emergentagent.com/job_ethicalwiki-client/artifacts/8745wi4r_sierrafarm-4.webp",
    sections: [
      {
        title: "Introduction",
        content: "A large technology company had a Wikipedia page focused almost exclusively on their controversies. These controversies were genuinely a significant part of the company's history. However, it wasn't right for controversies to be the main emphasis. The company contacted Ethical Wiki about creating a more complete, comprehensive, up-to-date, and balanced page."
      },
      {
        title: "The Rules",
        content: "Wikipedia pages should be representative summaries of the total literature on a topic. The Neutral Point of View policy covers 'due weight' - emphasis should be representative of weight in independent literature. For pages about living people, Biographies of Living Persons rules require trimming excessive controversial content. However, this doesn't apply to corporations. Editors can focus exclusively on controversies while leaving the rest undeveloped. Wikipedia's philosophy of incremental improvement means the only way to balance a company page is to write the rest of it."
      },
      {
        title: "The Reality",
        content: "Wikipedia's volunteer editors contribute based on enthusiasm. Pages about Taylor Swift or popular video games get more attention than business topics. Many company pages have controversies as almost all that's been written, because controversies prompt volunteer interest while company history does not."
      },
      {
        title: "The Strategy",
        content: "The controversies genuinely belonged in the article. The goal wasn't to significantly change them, but to develop 'the rest of the article' - filling a gap left by lack of volunteer interest."
      },
      {
        title: "The Execution",
        content: "We prepared a draft replacement that was more balanced and comprehensive, incorporating controversies into the chronology rather than making them the entire focus. With clear disclosure that it was prepared on behalf of the company, Wikipedia's editors agreed the prior version was highly imbalanced and unfair. They adopted our version after minor changes. The page later achieved 'Good Article' designation - among the top 1% on Wikipedia. A month later, a hostile editor arrived making derogatory remarks about corporate participation. Because our content was neutral and we were transparent, the editors we'd worked with came to our defense. The hostile editor made no changes."
      }
    ]
  }
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export default function CaseStudy() {
  const { slug } = useParams();
  const study = caseStudies[slug];

  if (!study) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-text-primary mb-4">Case Study Not Found</h1>
          <Link to="/#case-studies" className="text-accent hover:underline">Return to Case Studies</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={`${study.title} - Wikipedia Case Study`}
        description={study.subtitle}
        canonical={`/case-study/${slug}`}
        type="article"
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_df6f992c-82a6-44fb-a88f-450c0ac99b5b/artifacts/cvze4lab_q2y0upqj_75c9mfk0_Square%20Logo%20%28100%20x%20100%29.png" 
              alt="Ethical Wiki" 
              className="w-10 h-10"
              width="40"
              height="40"
              loading="eager"
            />
            <span className="font-heading text-xl text-gray-900 font-bold tracking-tight">Ethical Wiki</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/#about" className="text-gray-600 hover:text-accent text-sm uppercase tracking-widest">About</Link>
            <Link to="/#case-studies" className="text-gray-600 hover:text-accent text-sm uppercase tracking-widest">Case Studies</Link>
            <Link to="/#pricing" className="text-gray-600 hover:text-accent text-sm uppercase tracking-widest">Pricing</Link>
            <Link to="/#faq" className="text-gray-600 hover:text-accent text-sm uppercase tracking-widest">FAQ</Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div className="h-64 md:h-80 relative overflow-hidden">
          <img 
            src={study.image} 
            alt={study.title}
            className="w-full h-full object-cover"
            loading="eager"
            decoding="async"
            width="1200"
            height="320"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 -mt-20 relative z-10">
          <Link 
            to="/#case-studies" 
            className="inline-flex items-center text-accent hover:text-accent/80 mb-6 text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Case Studies
          </Link>
          
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <span className="text-accent text-sm uppercase tracking-[0.2em] mb-4 block">Case Study</span>
            <h1 className="font-heading text-4xl md:text-5xl text-text-primary mb-4">{study.title}</h1>
            <p className="text-text-secondary text-lg">{study.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-12">
            {study.sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-background-paper rounded-lg p-8 border border-gray-200"
              >
                <h2 className="font-heading text-2xl text-text-primary mb-4">{section.title}</h2>
                <p className="text-text-secondary leading-relaxed whitespace-pre-line">{section.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-background-paper">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-text-primary mb-6">Ready to Get Started?</h2>
          <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
            Schedule a consultation to discuss how Ethical Wiki can help with your Wikipedia presence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              className="bg-accent text-black hover:bg-accent/90 font-semibold px-6 py-3"
            >
              <Link to="/notability">
                Get a Wikipedia Assessment
                <ChevronRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-6 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_df6f992c-82a6-44fb-a88f-450c0ac99b5b/artifacts/cvze4lab_q2y0upqj_75c9mfk0_Square%20Logo%20%28100%20x%20100%29.png" 
              alt="Ethical Wiki" 
              className="w-6 h-6"
              width="24"
              height="24"
              loading="lazy"
            />
            <span className="text-gray-600 text-sm">© 2025 Ethical Wiki. All rights reserved.</span>
          </div>
          <nav className="flex gap-6">
            <Link to="/#about" className="text-gray-600 hover:text-accent text-sm">About</Link>
            <Link to="/#case-studies" className="text-gray-600 hover:text-accent text-sm">Case Studies</Link>
            <Link to="/#pricing" className="text-gray-600 hover:text-accent text-sm">Pricing</Link>
            <Link to="/#faq" className="text-gray-600 hover:text-accent text-sm">FAQ</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
